import * as KeyMap from '@packages/utilities/key-map';
import {type Lazy, defer, force} from '@packages/utilities/lazy';
import {type Pair, pair, getFirst, getSecond} from '@packages/utilities/Pair';

export type LazyList<a> = Lazy<ListStep<a>>;

type ListStep<a> =
  | {
      tag: 'Nil';
      val: Record<any, any>;
    }
  | {
      tag: 'Cons';
      val: {
        head: a;
        tail: LazyList<a>;
      };
    };

const _nil = {tag: 'Nil', val: {}} as const;
const nil = <a>(): ListStep<a> => _nil;
const cons = <a>(head: a, tail: LazyList<a>): ListStep<a> => ({
  tag: 'Cons',
  val: {head, tail},
});

const _empty = defer(() => _nil);
export const empty = <a>(): LazyList<a> => _empty as any;

export const singleton = <a>(x: a): LazyList<a> =>
  defer(() => cons(x, empty()));

export const isEmpty = <a>(list: LazyList<a>): boolean =>
  force(list).tag === 'Nil';

export const isNotEmpty = <a>(list: LazyList<a>): boolean =>
  force(list).tag === 'Cons';

/**
 * Creates a LazyList using the provided item producing function and an initial
 * seed value. The producer function receives the seed value and must return
 * undefined if it can’t produce an item, or a pair consisting of an item and
 * a new seed to be used to produce the next item.
 */
export const produce = <a, b>(
  producer: (arg1: b) => undefined | Pair<a, b>,
  seed: b
): LazyList<a> =>
  defer(() => {
    const res = producer(seed);
    if (res === undefined) return nil();
    const item = getFirst(res);
    const nextSeed = getSecond(res);
    return cons(item, produce(producer, nextSeed));
  });

/**
 * Analogous to `Array.reduce`, this function takes a list, a reducer and an
 * initial value. The reducer is called for every item in the list to accumulate
 * a final value. This function by nature forces the evaluation of all items.
 */
export const reduce = <a, b>(
  list: LazyList<a>,
  reducer: (arg1: b, arg2: a) => b,
  init: b
): b => {
  const step = force(list);
  if (step.tag === 'Nil') return init;
  const {head, tail} = step.val;
  return reduce(tail, reducer, reducer(init, head));
};

/**
 * Creates a LazyList out of an Array
 */
export const fromArray = <a>(xs: Array<a>): LazyList<a> =>
  // @ts-expect-error - TS2322 - Type 'LazyList<a | undefined>' is not assignable to type 'LazyList<a>'.
  produce(
    (index) => (index >= xs.length ? undefined : pair(xs[index], index + 1)),
    0
  );

/**
 * Converts a LazyList to an Array. By nature this function forces all items to
 * be evaluated.
 */
export const toArray = <a>(xs: LazyList<a>): Array<a> => {
  const res: Array<a> = [];
  let curr = xs;
  while (curr) {
    const step = force(curr);
    if (step.tag === 'Nil') return res;
    const {head, tail} = step.val;
    res.push(head);
    curr = tail;
  }
  return res;
};

/**
 * Returns the first item in the list, or `undefined` if the list is empty.
 */
export const first = <a>(xs: LazyList<a>): undefined | a => {
  const step = force(xs);
  if (step.tag === 'Nil') return undefined;
  return step.val.head;
};

/**
 * Truncates the list to the first `count` items.
 */
export const take = <a>(count: number, xs: LazyList<a>): LazyList<a> =>
  count <= 0
    ? empty()
    : defer(() => {
        const step = force(xs);
        if (step.tag === 'Nil') return nil();
        const {head, tail} = step.val;
        return cons(head, take(count - 1, tail));
      });

/**
 * Attaches the given item to the beginning of the list.
 */
export const prepend = <a>(x: a, xs: LazyList<a>): LazyList<a> =>
  defer(() => cons(x, xs));

/**
 * Analogous to `Array.map`, this function applies the provided function to
 * each item in the list.
 */
export const map = <a, b>(list: LazyList<a>, fn: (arg1: a) => b): LazyList<b> =>
  defer(() => {
    const step = force(list);
    if (step.tag === 'Nil') return nil();
    const {head, tail} = step.val;
    return cons(fn(head), map(tail, fn));
  });

/**
 * Analogous to `Array.flatMap`, this function applies the provided function to
 * each item in the list and then concatenates the results.
 */
export const flatMap = <a, b>(
  list: LazyList<a>,
  fn: (arg1: a) => LazyList<b>
): LazyList<b> =>
  defer(() => {
    const step = force(list);
    if (step.tag === 'Nil') return nil();
    const {head, tail} = step.val;
    return force(concat(fn(head), flatMap(tail, fn)));
  });

/**
 * Analogous to `Array.filter`, this function discards list items for which the
 * provided predicate returns `false`;
 */
export const filter = <a>(
  list: LazyList<a>,
  predicate: (arg1: a) => boolean
): LazyList<a> =>
  defer(() => {
    const step = force(list);
    if (step.tag === 'Nil') return nil();
    const {head, tail} = step.val;
    const filteredTail = filter(tail, predicate);
    return predicate(head) ? cons(head, filteredTail) : force(filteredTail);
  });

/**
 * Analogous to `Array.concat`, this function concatenates two lists.
 */
export const concat = <a>(xs: LazyList<a>, ys: LazyList<a>): LazyList<a> =>
  defer(() => {
    const step = force(xs);
    if (step.tag === 'Nil') return force(ys);
    const {head, tail} = step.val;
    return cons(head, concat(tail, ys));
  });

/**
 * Creates a list of entries (key+value pairs) from a POJO map.
 */
export const entries = <v>(
  strmap: KeyMap.Type<v>
): LazyList<Pair<string, v>> => {
  const keys = KeyMap.keys(strmap);

  return produce((index) => {
    const key = keys[index];
    if (key === undefined) return undefined;
    const val = strmap[key]!;
    return pair(pair(key, val), index + 1);
  }, 0);
};

/**
 * Finds a value by key in a list of key+value pairs.
 */
export const lookup = <k extends string, v>(
  pairs: LazyList<Pair<k, v>>,
  key: k
): undefined | v => {
  const step = force(pairs);
  if (step.tag === 'Nil') return undefined;
  const {head, tail} = step.val;
  if (getFirst(head) === key) return getSecond(head);
  return lookup(tail, key);
};
